/** API Access queries: personal keys, Claude sessions, the application guide, API Feedback
 * and API usage.
 *
 * These endpoints publish real response schemas (2026-10-29), so the types below come from
 * the generated schema rather than being restated by hand.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'
import type { components } from '@/lib/api/schema'

type Schemas = components['schemas']
export type ApiKey = Schemas['ApiKeyOut']
export type ApiKeyCreated = Schemas['ApiKeyCreated']
export type Scopes = Schemas['ScopesOut']
export type Network = Schemas['NetworkOut']
export type AssistantSession = Schemas['SessionOut']
export type ServicePrincipalGuide = Schemas['ServicePrincipalOut']
export type ApiFeedbackItem = Schemas['FeedbackOut']
export type ApiFeedbackList = Schemas['FeedbackList']
export type ApiUsage = Schemas['UsageOut']
export type NewKey = Schemas['ApiKeyCreate']

const keys = {
  mine: ['api-keys', 'mine'] as const,
  all: ['api-keys', 'all'] as const,
  scopes: ['api-keys', 'scopes'] as const,
  network: ['api-keys', 'network'] as const,
  sp: ['api-keys', 'service-principal'] as const,
  feedback: (filters: object) => ['api-feedback', filters] as const,
  usage: (days: number) => ['api-usage', days] as const,
}

export function useScopes() {
  return useQuery({
    queryKey: keys.scopes,
    queryFn: async () => (await api.GET('/api/v1/api-keys/scopes', {})).data as Scopes,
    staleTime: Infinity,
  })
}

export function useNetwork() {
  return useQuery({
    queryKey: keys.network,
    queryFn: async () => (await api.GET('/api/v1/api-keys/network', {})).data as Network,
  })
}

export function useMyKeys() {
  return useQuery({
    queryKey: keys.mine,
    queryFn: async () => ((await api.GET('/api/v1/api-keys', {})).data?.items ?? []),
  })
}

/** Administrators only — `enabled` keeps everyone else from writing a denial on each visit. */
export function useAllKeys(enabled: boolean) {
  return useQuery({
    queryKey: keys.all,
    enabled,
    queryFn: async () =>
      ((await api.GET('/api/v1/api-keys/all', {})).data?.items ?? []),
  })
}

export function useCreateKey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: NewKey) =>
      (await api.POST('/api/v1/api-keys', { body })).data as ApiKeyCreated,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })
}

export function useRevokeKey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, any }: { id: string; any: boolean }) =>
      any
        ? (await api.DELETE('/api/v1/api-keys/all/{key_id}', { params: { path: { key_id: id } } }))
            .data
        : (await api.DELETE('/api/v1/api-keys/{key_id}', { params: { path: { key_id: id } } }))
            .data,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })
}

export function useStartSession() {
  return useMutation({
    mutationFn: async (minutes: number) =>
      (await api.POST('/api/v1/api-keys/assistant-session', { body: { minutes } }))
        .data as AssistantSession,
  })
}

export function useServicePrincipalGuide(enabled: boolean) {
  return useQuery({
    queryKey: keys.sp,
    enabled,
    queryFn: async () =>
      (await api.GET('/api/v1/service-principal-prompt', {})).data as ServicePrincipalGuide,
  })
}

export interface FeedbackFilters {
  resolved?: boolean
  category?: string
  severity?: string
  offset: number
}

export function useApiFeedback(filters: FeedbackFilters, enabled = true) {
  return useQuery({
    queryKey: keys.feedback(filters),
    enabled,
    queryFn: async () =>
      (
        await api.GET('/api/v1/api-feedback', {
          params: { query: { ...filters, limit: 25 } as never },
        })
      ).data as ApiFeedbackList,
  })
}

export function useResolveApiFeedback() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      resolved,
      note,
    }: {
      id: string
      resolved: boolean
      note?: string
    }) =>
      (
        await api.PATCH('/api/v1/api-feedback/{feedback_id}', {
          params: { path: { feedback_id: id } },
          body: { resolved, resolutionNote: note ?? null },
        })
      ).data as ApiFeedbackItem,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['api-feedback'] })
    },
  })
}

export function useApiUsage(days: number, enabled = true) {
  return useQuery({
    queryKey: keys.usage(days),
    enabled,
    queryFn: async () =>
      (await api.GET('/api/v1/admin/api-usage', { params: { query: { days } } })).data as ApiUsage,
  })
}
